import React, { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { Autocomplete, Button, IconButton, Stack, TextField } from "@mui/material";
import { debounce, startCase } from "lodash";
import { useReportsMetaSelector } from "store/selectors/reports-selectors";

import { State } from "./CreateReportSection";

interface Props {
  data: State["kpi"];
  setData: (cb: (prevState: State["kpi"]) => State["kpi"]) => void;
}

export const KPI: React.FC<Props> = ({ data, setData }) => {
  const addKPI = () => {
    setData((prevState) => {
      const maxId = prevState.reduce<number>((acc, item) => {
        const currentItemId = Number(item.id);
        return currentItemId > acc ? currentItemId : acc;
      }, 0);

      return [...prevState, { id: String(maxId + 1), title: "", filter: "", isDeleted: false }];
    });
  };

  const handleChangeValue = ({ id, filter }: { id: number | string; filter: string }) => {
    setData((prevState) => {
      return prevState.map((item) =>
        item.id === id ? { ...item, filter, ...(filter !== item.title && { title: filter }) } : item
      );
    });
  };

  const handleChangeName = ({ id, title }: { id: number | string; title: string }) => {
    setData((prevState) => {
      return prevState.map((item) => (item.id === id ? { ...item, title } : item));
    });
  };

  const handleDelete = ({ id }: { id: number | string }) => {
    // if it is an item on UI, just remove it
    if (typeof id === "string") {
      setData((prevState) => prevState.filter((item) => item.id !== id));
      // if it is an item with number id, it means it is in the DB, so we need to mark it as deleted to hint BE
    } else {
      setData((prevState) => prevState.map((item) => (item.id === id ? { ...item, isDeleted: true } : item)));
    }
  };

  const handleUndoDelete = ({ id }: { id: number | string }) => {
    setData((prevState) => prevState.map((item) => (item.id === id ? { ...item, isDeleted: false } : item)));
  };

  return (
    <Stack sx={{ gap: "14px", minWidth: 425, width: 425 }}>
      {data.map((item) => (
        <Item
          key={item.id}
          item={item}
          onChangeValue={handleChangeValue}
          onChangeName={handleChangeName}
          onDelete={handleDelete}
          onUndoDelete={handleUndoDelete}
          disabledDelete={data.length < 2}
        />
      ))}
      <Button onClick={addKPI} variant="text" sx={{ alignSelf: "flex-start" }} startIcon={<AddIcon />}>
        Add KPI
      </Button>
    </Stack>
  );
};

interface ItemProps {
  item: State["kpi"][number];
  onChangeValue: (data: { id: number | string; filter: string }) => void;
  onChangeName: (data: { id: number | string; title: string }) => void;
  onDelete: (data: { id: number | string }) => void;
  onUndoDelete: (data: { id: number | string }) => void;
  disabledDelete?: boolean;
}

const Item: React.FC<ItemProps> = ({ item, onChangeValue, onChangeName, onDelete, onUndoDelete, disabledDelete }) => {
  const { KPIFields } = useReportsMetaSelector();

  const [name, setName] = useState<string>(item.title);

  useEffect(() => {
    if (item.title === name) return;
    setName(item.title);
  }, [item.title]);

  const handleChangeNameDebounced = useCallback(
    debounce((title: string) => onChangeName({ id: item.id, title }), 1000),
    [onChangeName]
  );

  return (
    <Stack sx={{ gap: "10px", flexDirection: "row", alignItems: "center" }}>
      <Autocomplete
        fullWidth
        options={KPIFields}
        disabled={item.isDeleted}
        renderInput={(params) => (
          <TextField
            {...params}
            label="KPI"
            {...(item.isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
          />
        )}
        renderOption={(props, option) => <li {...props}>{startCase(option as string)}</li>}
        value={item.filter}
        disableClearable
        onChange={(_, value) => onChangeValue({ id: item.id, filter: value })}
      />
      <TextField
        label="Display Name"
        fullWidth
        disabled={item.isDeleted}
        value={name}
        {...(item.isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
        onChange={(e) => {
          const { value } = e.target;
          setName(value);
          handleChangeNameDebounced(value);
        }}
      />
      {item.isDeleted ? (
        <IconButton onClick={() => onUndoDelete({ id: item.id })}>
          <UndoIcon />
        </IconButton>
      ) : (
        <IconButton disabled={disabledDelete} onClick={() => onDelete({ id: item.id })}>
          <CloseIcon />
        </IconButton>
      )}
    </Stack>
  );
};
